/**
 * Fila de revisão de identidade (H12) — o degrau 6 da §5.2, na tela.
 *
 * ── A armadilha, e a decisão de desenho que a fecha ───────────────────────
 *
 * *"Pré-selecionar o sobrevivente. O revisor confirma sem ler, o cliente errado
 * é mesclado, e desfazer exige split — que cria uma terceira identidade."*
 *
 * Nenhum dos dois lados nasce escolhido. O botão de aprovar não fica cinza e
 * mudo: ele diz *"escolha qual dos dois fica"*, porque botão desabilitado sem
 * motivo é o que faz a pessoa clicar de novo achando que travou.
 *
 * E aqui EXISTE confirmação, ao contrário do encerrar conversa (D08). A
 * diferença é a volta: encerrar se desfaz pelo mesmo botão; um merge se desfaz
 * por split, e o split cria uma identidade NOVA — o cliente não volta ao que
 * era. Confirmação em ação reversível treina a ignorar; nesta, é o único
 * momento em que a pessoa lê o que vai acontecer.
 *
 * ── Por que a tela mostra tanto antes de deixar decidir ───────────────────
 *
 * A Técnica: *"score sozinho leva a aprovação no chute — e merge indevido exige
 * split, que é caro"*. Então a comparação vem primeiro e a decisão depois: os
 * dois clientes lado a lado com o que decide (contato, conversas,
 * oportunidades, vínculos de origem, última interação), e a barra de sinais
 * mostrando de onde veio cada pedaço do score.
 */

import { useCallback, useState, type ReactNode } from 'react';
import type { CandidatoDeMerge, DetalheDoCandidato } from '@cliqcrm/contracts';
import { useApi, useCandidatoDeMerge, useCandidatosDeMerge } from '@/shared/api/consultas';
import { useInvalidar } from '@/shared/api/provedor';
import { useSessao } from '@/shared/sessao/contexto';
import { Carregando, Erro, Estado } from '@/shared/ui/estados/estados';
import { idadeEmPalavras, instantePorExtenso } from '@/shared/ui/estados/idade';
import { useDecisao, type Decisao } from '../model/decisao';
import './identidade.css';
import { Botao, CabecalhoDeTela, CampoArea, Cartao, Ficha, Segmentado, Selo } from '@/shared/ui';
import { nomeDoProvedor } from '@/entities/integracoes/provedor';
import { Faixa } from '@/shared/ui/estados/faixa';

const NOMES_DE_SINAL: Record<string, string> = {
  nome: 'Nome',
  empresa: 'Empresa',
  /*
   * O valor continua sendo `veiculo_interesse` — é o sinal gravado, e mudá-lo
   * custaria migração dos registros de comparação já escritos. O que muda é o
   * que se lê: numa clínica, "Veículo de interesse" não quer dizer nada, e o
   * dado por trás é o atributo livre do cadastro.
   */
  veiculo_interesse: 'Item de interesse',
  origem: 'Origem',
  /*
   * O único sinal que não vem do score (D23). Ele aparece quando alguém
   * informou um CPF ou CNPJ que já existia em outro cadastro — e é o motivo
   * mais decisivo que esta fila mostra.
   */
  documento: 'Documento (CPF/CNPJ)',
};

const NOMES_DE_STATUS: Record<string, string> = {
  merge_candidate: 'Aguardando revisão',
  aprovado: 'Juntados',
  rejeitado: 'Mantidos separados',
  expirado: 'Expirado',
};

type Aba = 'fila' | 'historico';

export function RevisaoDeIdentidade(): ReactNode {
  const [aba, definirAba] = useState<Aba>('fila');
  const [aberto, definirAberto] = useState<string | null>(null);

  const consulta = useCandidatosDeMerge(
    aba === 'fila' ? {} : { status: 'aprovado,rejeitado,expirado' },
  );

  return (
    <div className="identidade">
      <CabecalhoDeTela
        titulo="Revisão de identidade"
        apoio="Quando o sistema suspeita que dois cadastros são a mesma pessoa e não tem certeza suficiente para juntar sozinho, ele pergunta aqui."
        acoes={
          <Segmentado<Aba>
            rotulo="O que mostrar"
            opcoes={[
              { valor: 'fila', rotulo: 'Aguardando revisão' },
              { valor: 'historico', rotulo: 'Já decididos' },
            ]}
            escolhida={aba}
            aoEscolher={(chave) => {
              definirAba(chave);
              definirAberto(null);
            }}
          />
        }
      />

      {/* O corpo rola; o cabeçalho fica — com ele, a escolha da aba. */}
      <div className="identidade__corpo">
        <Estado
          consulta={consulta}
          forma="lista"
          linhas={4}
          vazio={{
            quando: (dados) => dados.itens.length === 0,
            titulo: aba === 'fila' ? 'Nada esperando decisão' : 'Nenhuma decisão ainda',
            porque:
              aba === 'fila'
                ? 'Quando o sistema encontrar dois cadastros parecidos e não tiver certeza, eles ' +
                  'aparecem aqui para você decidir.'
                : 'As junções que você aprovar ou recusar ficam registradas nesta aba.',
          }}
        >
          {(dados) => (
            <ul className="fila-identidade">
              {dados.itens.map((item) => (
                <li key={item.id}>
                  <LinhaDaFila
                    item={item}
                    limiar={dados.limiar}
                    aberto={aberto === item.id}
                    aoAlternar={() => definirAberto(aberto === item.id ? null : item.id)}
                    aoDecidir={() => definirAberto(null)}
                  />
                </li>
              ))}
            </ul>
          )}
        </Estado>
      </div>
    </div>
  );
}

/* ── A linha, e o detalhe que ela abre ───────────────────────────────────── */

function LinhaDaFila({
  item,
  limiar,
  aberto,
  aoAlternar,
  aoDecidir,
}: {
  item: CandidatoDeMerge;
  limiar: number;
  aberto: boolean;
  aoAlternar: () => void;
  aoDecidir: () => void;
}): ReactNode {
  const pendente = item.status === 'merge_candidate';
  const [primeiro, segundo] = item.lados;

  return (
    <article className="candidato" data-aberto={aberto ? 'sim' : undefined}>
      <button
        type="button"
        className="candidato__resumo"
        aria-expanded={aberto}
        onClick={aoAlternar}
      >
        <Semelhanca score={item.score} limiar={limiar} />

        <span className="candidato__par">
          <b>{primeiro?.nome_exibicao ?? 'Sem nome'}</b>
          <span className="candidato__vs">e</span>
          <b>{segundo?.nome_exibicao ?? 'Sem nome'}</b>
        </span>

        <span className="candidato__quando">
          {pendente ? (
            <>encontrado {idadeEmPalavras(item.criado_em) ?? 'há pouco'}</>
          ) : (
            <Selo tom={item.status === 'aprovado' ? 'ok' : 'neutro'}>
              {NOMES_DE_STATUS[item.status] ?? item.status}
            </Selo>
          )}
        </span>
      </button>

      {aberto && (
        <div className="candidato__detalhe">
          {pendente ? (
            <Comparacao candidatoId={item.id} aoDecidir={aoDecidir} />
          ) : (
            <DecisaoTomada item={item} />
          )}
        </div>
      )}
    </article>
  );
}

/**
 * O score em palavras, e não só o número.
 *
 * "0,72" não diz nada a quem não conhece a régua; "parecidos" diz. O número
 * fica no `title` para quem quiser conferir, e a comparação com o limiar
 * aparece no detalhe, onde há espaço para explicá-la.
 */
function Semelhanca({ score, limiar }: { score: number; limiar: number }): ReactNode {
  const porcento = Math.round(score * 100);
  const forte = score >= limiar * 0.8;

  return (
    <span
      className={`semelhanca${forte ? ' semelhanca--forte' : ''}`}
      title={`Semelhança calculada: ${score.toFixed(2)}. A organização só junta sozinha a partir de ${limiar.toFixed(2)}.`}
    >
      {porcento}% parecidos
    </span>
  );
}

function DecisaoTomada({ item }: { item: CandidatoDeMerge }): ReactNode {
  return (
    <Ficha
      larguraRotulo="auto"
      linhas={[
        {
          rotulo: 'Decidido',
          valor: idadeEmPalavras(item.decidido_em) ?? 'sem data registrada',
          titulo: instantePorExtenso(item.decidido_em) ?? undefined,
        },
        ...(item.motivo ? [{ rotulo: 'Motivo', valor: item.motivo }] : []),
      ]}
    />
  );
}

/* ── Comparação e decisão ────────────────────────────────────────────────── */

function Comparacao({
  candidatoId,
  aoDecidir,
}: {
  candidatoId: string;
  aoDecidir: () => void;
}): ReactNode {
  const api = useApi();
  const invalidar = useInvalidar();
  const consulta = useCandidatoDeMerge(candidatoId);
  const decisao = useDecisao(api, candidatoId, invalidar, aoDecidir);
  const { estado } = useSessao();
  /** A clínica (#811): a ficha e os convênios só aparecem com ela ligada. */
  const clinica = estado.nome === 'autenticado' && (estado.me.flags['clinica'] ?? false);

  if (consulta.isPending) return <Carregando forma="ficha" linhas={4} />;
  if (consulta.error && consulta.data === undefined) {
    return <Erro erro={consulta.error} aoRepetir={() => void consulta.refetch()} />;
  }
  if (!consulta.data) return <Carregando forma="ficha" linhas={4} />;

  const detalhe = consulta.data;
  const duasFichas = detalhe.lados.every((lado) => lado.tem_ficha_do_paciente);

  return (
    <>
      <PorQueSuspeitou detalhe={detalhe} />

      <div className="lados">
        {detalhe.lados.map((lado) => (
          <Lado
            key={lado.cliente_id}
            lado={lado}
            clinica={clinica}
            divergentes={divergencias(detalhe.lados)}
            escolhido={decisao.sobrevivente === lado.cliente_id}
            aoEscolher={() => decisao.escolherSobrevivente(lado.cliente_id)}
          />
        ))}
      </div>

      {/*
        A clínica (#811): com duas fichas, vale a do que fica, completada pela do
        outro, e o que não couber se perde. Dito ANTES de aprovar, porque depois
        não há como reconstruir.
      */}
      {clinica && duasFichas && (
        <Faixa tom="atencao">
          Os dois cadastros têm ficha do paciente. Fica a ficha de quem sobreviver: o que estiver
          vazio nela é completado pela outra, e o que não couber se perde. Os convênios e os
          documentos dos dois passam para quem sobreviver.
        </Faixa>
      )}

      <Acoes decisao={decisao} />
    </>
  );
}

/**
 * Por que o sistema suspeitou — a Técnica da task, literal.
 *
 * A barra mostra a contribuição de cada sinal em relação ao PESO dele, e não em
 * relação ao total: um sinal que vale 0,35 e contribuiu 0,35 está cheio, mesmo
 * representando um terço do score. É o que responde "esse pedaço bateu ou não",
 * que é a pergunta do revisor.
 */
function PorQueSuspeitou({ detalhe }: { detalhe: DetalheDoCandidato }): ReactNode {
  const relevantes = detalhe.sinais.filter((sinal) => sinal.peso > 0);
  const porDocumento = detalhe.sinais.some((sinal) => sinal.sinal === 'documento');

  return (
    <section className="porque">
      <h3>Por que o sistema achou parecido</h3>

      <ul className="sinais">
        {relevantes.map((sinal) => {
          const preenchido = sinal.peso === 0 ? 0 : (sinal.contribuicao / sinal.peso) * 100;
          return (
            <li key={sinal.sinal} className="sinal">
              <span className="sinal__nome">{NOMES_DE_SINAL[sinal.sinal] ?? sinal.sinal}</span>
              <span
                className="sinal__barra"
                role="img"
                aria-label={`${NOMES_DE_SINAL[sinal.sinal] ?? sinal.sinal}: ${Math.round(preenchido)}% de semelhança`}
              >
                <span className="sinal__preenchido" style={{ width: `${preenchido}%` }} />
              </span>
              <span className="sinal__valor">{Math.round(preenchido)}%</span>
            </li>
          );
        })}
      </ul>

      {/*
        A explicação MUDA quando a suspeita veio do documento.

        A frase do limiar diz "esta revenda junta sozinha a partir de X% — por
        isso este par veio parar aqui", e ela só faz sentido para o score
        probabilístico, que veio ABAIXO do limiar. Documento vale 0,95, quase
        sempre ACIMA — e mesmo assim nunca funde sozinho, por decisão da C10.

        Deixar a frase do limiar aqui diria a quem revisa que o sistema achou
        pouco parecido, quando ele achou o contrário: achou a prova mais forte
        que tem, e parou de propósito.
      */}
      {porDocumento ? (
        <p className="porque__regra">
          Os dois cadastros têm o <b>mesmo documento</b>. É o sinal mais forte que existe aqui — e
          mesmo assim o sistema não junta sozinho: um dígito digitado errado transformaria duas
          pessoas em uma, e desfazer isso é o caminho mais caro do sistema.
        </p>
      ) : (
        <p className="porque__regra">
          No total, {Math.round(detalhe.score * 100)}% de semelhança. Esta organização junta
          cadastros sozinha a partir de {Math.round(detalhe.limiar * 100)}% — por isso este par veio
          parar aqui.
        </p>
      )}
    </section>
  );
}

/**
 * Quais campos DIFEREM entre os dois lados.
 *
 * ── Por que isto é mais que estética ──────────────────────────────────────
 *
 * A tela mostrava as duas fichas com a mesma aparência, e cabia a quem decide
 * comparar campo a campo com o olho. Numa decisão de FUNDIR cadastros, deixar
 * passar um telefone diferente é juntar duas pessoas — e desfazer isso é o
 * caminho mais caro da §5.2.
 *
 * Compara só o que é comparável: contagem de conversas e de oportunidades
 * divergir é esperado (são cadastros diferentes, com históricos diferentes) e
 * marcá-las pintaria a tela inteira, ensinando a ignorar a marca.
 */
const CAMPOS_COMPARAVEIS = ['telefone_mascarado', 'email_mascarado', 'nome_exibicao'] as const;

function divergencias(lados: DetalheDoCandidato['lados']): ReadonlySet<string> {
  const diferentes = new Set<string>();
  if (lados.length < 2) return diferentes;

  for (const campo of CAMPOS_COMPARAVEIS) {
    const valores = lados.map((l) => l[campo] ?? null);
    // Ausente dos dois lados não é divergência: é falta de dado, e marcar isso
    // apontaria para onde não há o que decidir.
    if (valores.every((v) => v === null)) continue;
    if (new Set(valores).size > 1) diferentes.add(campo);
  }
  return diferentes;
}

function Lado({
  lado,
  clinica,
  divergentes,
  escolhido,
  aoEscolher,
}: {
  lado: DetalheDoCandidato['lados'][number];
  /** A clínica (#811): mostra a ficha e os convênios de cada lado. */
  clinica: boolean;
  divergentes: ReadonlySet<string>;
  escolhido: boolean;
  aoEscolher: () => void;
}): ReactNode {
  return (
    <Cartao como="label" variante="escolhivel" escolhido={escolhido}>
      {/*
        Radio de verdade, e não dois botões que fingem escolha.
        Com `name` compartilhado, o teclado navega entre os dois com as setas e
        o leitor de tela anuncia "1 de 2" — comportamento que dois botões
        precisariam imitar à mão, e imitariam mal.
      */}
      <input
        type="radio"
        name="sobrevivente"
        className="lado__escolha"
        checked={escolhido}
        onChange={aoEscolher}
      />

      <div className="lado__corpo">
        <b
          className="lado__nome"
          data-destaque={divergentes.has('nome_exibicao') ? 'sim' : undefined}
        >
          {lado.nome_exibicao ?? 'Cadastro sem nome'}
        </b>

        <Ficha
          larguraRotulo="auto"
          direita
          linhas={[
            {
              rotulo: 'Telefone',
              valor: lado.telefone_mascarado ?? '—',
              destaque: divergentes.has('telefone_mascarado'),
            },
            {
              rotulo: 'E-mail',
              valor: lado.email_mascarado ?? '—',
              destaque: divergentes.has('email_mascarado'),
            },
            { rotulo: 'Conversas', valor: lado.conversas },
            { rotulo: 'Oportunidades', valor: lado.oportunidades },
            ...(clinica
              ? [
                  {
                    rotulo: 'Ficha do paciente',
                    valor: lado.tem_ficha_do_paciente ? 'preenchida' : 'não tem',
                  },
                  { rotulo: 'Convênios', valor: lado.convenios },
                  { rotulo: 'Documentos do paciente', valor: lado.documentos_do_paciente },
                  { rotulo: 'Exames do paciente', valor: lado.exames_do_paciente },
                ]
              : []),
            {
              rotulo: 'Última interação',
              valor: idadeEmPalavras(lado.ultima_interacao_em) ?? 'nenhuma',
              titulo: instantePorExtenso(lado.ultima_interacao_em) ?? undefined,
            },
            {
              rotulo: 'Cadastrado',
              valor: idadeEmPalavras(lado.criado_em) ?? '—',
              titulo: instantePorExtenso(lado.criado_em) ?? undefined,
            },
          ]}
        />

        {lado.vinculos.length > 0 && (
          <p className="lado__vinculos">
            {/*
              O sistema de origem pelo nome da TELA.

              `source_system` é o identificador de quem cunhou o vínculo, e vem
              cru do banco: `chatwoot`, em minúsculo. Quem está decidindo se
              duas fichas são a mesma pessoa não tem o que fazer com o nome do
              produto que rodou por baixo.
            */}
            Veio de: {lado.vinculos.map((v) => nomeDoProvedor(v.source_system)).join(', ')}
          </p>
        )}
      </div>

      <span className="lado__marca">{escolhido ? 'Este fica' : 'Escolher este'}</span>
    </Cartao>
  );
}

/* ── As ações ────────────────────────────────────────────────────────────── */

function Acoes({ decisao }: { decisao: Decisao }): ReactNode {
  const [recusando, definirRecusando] = useState(false);
  const [motivo, definirMotivo] = useState('');

  const enviando = decisao.estado.nome === 'enviando';

  const recusar = useCallback(() => {
    if (motivo.trim() === '') return;
    decisao.rejeitar(motivo.trim());
  }, [decisao, motivo]);

  if (decisao.confirmando) {
    return (
      <div className="acoes acoes--confirmar" role="alertdialog" aria-label="Confirmar a junção">
        <p>
          <b>Isto não tem desfazer simples.</b> Os dois cadastros viram um só, e o histórico do
          outro passa para o que você escolheu. Separar depois cria um <b>terceiro</b> cadastro — o
          antigo não volta.
        </p>
        <div className="acoes__botoes">
          <Botao
            variante="perigo"
            onClick={decisao.aprovar}
            desabilitado={enviando}
            motivo="Aguarde: a decisão está sendo enviada."
          >
            {enviando ? 'Juntando…' : 'Sim, juntar os dois'}
          </Botao>
          <Botao
            onClick={decisao.cancelar}
            desabilitado={enviando}
            motivo="Aguarde: a decisão está sendo enviada."
          >
            Voltar
          </Botao>
        </div>
      </div>
    );
  }

  if (recusando) {
    return (
      <div className="acoes acoes--recusar">
        <label className="acoes__rotulo" htmlFor="motivo">
          Por que não são a mesma pessoa?
        </label>
        {/*
          O motivo é obrigatório, e a tela diz para que ele serve. Sem isso, o
          campo parece burocracia e vira "não é" — que não ajuda ninguém a
          calibrar nada depois.
        */}
        <p className="acoes__ajuda">
          Isso ajuda a ajustar o sistema. "São irmãos, mesmo sobrenome" e "é a mesma pessoa, mas
          prefiro não juntar agora" levam a ajustes diferentes.
        </p>
        <CampoArea
          id="motivo"
          rows={2}
          value={motivo}
          onChange={(e) => definirMotivo(e.target.value)}
          autoFocus
        />
        <div className="acoes__botoes">
          <Botao
            variante="primario"
            onClick={recusar}
            desabilitado={enviando || motivo.trim() === ''}
            motivo={
              motivo.trim() === ''
                ? 'Escreva o motivo para continuar.'
                : 'Aguarde: a decisão está sendo enviada.'
            }
          >
            {enviando ? 'Salvando…' : 'Manter separados'}
          </Botao>
          <Botao
            onClick={() => definirRecusando(false)}
            desabilitado={enviando}
            motivo="Aguarde: a decisão está sendo enviada."
          >
            Voltar
          </Botao>
        </div>
        {decisao.estado.nome === 'falhou' && <FalhaDaDecisao />}
      </div>
    );
  }

  const semEscolha = decisao.sobrevivente === null;

  return (
    <div className="acoes">
      <div className="acoes__botoes">
        {/*
          Desabilitado COM motivo visível, e não um botão cinza mudo.
          A escolha não tem padrão (a armadilha da task), então a tela precisa
          dizer o que falta — senão a pessoa clica de novo achando que travou.
        */}
        <Botao
          variante="primario"
          onClick={decisao.pedirConfirmacao}
          desabilitado={semEscolha || enviando}
          motivo={
            semEscolha
              ? 'Escolha qual dos dois cadastros fica.'
              : 'Aguarde: a decisão está sendo enviada.'
          }
        >
          Juntar os dois
        </Botao>
        <Botao
          onClick={() => definirRecusando(true)}
          desabilitado={enviando}
          motivo="Aguarde: a decisão está sendo enviada."
        >
          Não são a mesma pessoa
        </Botao>
      </div>

      {semEscolha && (
        <p className="acoes__falta" role="status">
          Escolha acima qual dos dois cadastros fica. Ele recebe o histórico do outro.
        </p>
      )}

      {decisao.estado.nome === 'falhou' && <FalhaDaDecisao />}
    </div>
  );
}

function FalhaDaDecisao(): ReactNode {
  return (
    <Faixa tom="bloqueado">
      Não foi possível salvar a decisão. Nada foi alterado — tente de novo.
    </Faixa>
  );
}

/** Reexportado para a casca não precisar conhecer o nome interno. */
export { RevisaoDeIdentidade as Identidade };
